import * as React from 'react'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { LucideIcon, Plus, Download, Upload, Settings, RefreshCw } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface QuickAction {
  label: string
  icon: LucideIcon
  onClick?: () => void
  variant?: 'default' | 'outline' | 'secondary' | 'ghost'
  color?: 'gold' | 'purple' | 'blue' | 'green' | 'orange'
  disabled?: boolean
}

export interface QuickActionsProps {
  title?: string
  actions?: QuickAction[]
  layout?: 'grid' | 'row'
  showCard?: boolean
  className?: string
}

const colorMap = {
  gold: 'bg-[#f5b800] text-black hover:bg-[#f59e0b]',
  purple: 'bg-[#9333ea] text-white hover:bg-[#3b82f6]',
  blue: 'bg-[#3b82f6] text-white hover:bg-[#9333ea]',
  green: 'bg-[#10b981] text-white',
  orange: 'bg-[#f59e0b] text-black hover:bg-[#f5b800]'
}

const defaultActions: QuickAction[] = [
  { label: 'New Project', icon: Plus, color: 'gold' },
  { label: 'Import', icon: Upload, color: 'purple' },
  { label: 'Export', icon: Download, color: 'blue' },
  { label: 'Refresh', icon: RefreshCw, variant: 'outline' },
  { label: 'Settings', icon: Settings, variant: 'ghost' }
]

export const QuickActions = React.forwardRef<HTMLDivElement, QuickActionsProps>(
  ({ 
    title = 'Quick Actions', 
    actions = defaultActions, 
    layout = 'grid', 
    showCard = true, 
    className, 
    ...props 
  }, ref) => {
    const renderActions = () => (
      <div 
        className={cn(
          layout === 'grid' 
            ? 'grid grid-cols-2 gap-3' 
            : 'flex flex-wrap gap-2',
          !showCard && className
        )}
      >
        {actions.map((action, index) => (
          <Button
            key={index}
            variant={action.variant || 'default'}
            size="sm"
            className={cn(
              'flex items-center gap-2',
              layout === 'grid' ? 'w-full justify-start' : '',
              action.color && !action.variant ? colorMap[action.color] : ''
            )}
            onClick={action.onClick}
            disabled={action.disabled}
          >
            <action.icon className="h-4 w-4" />
            {action.label}
          </Button>
        ))}
      </div>
    )

    if (!showCard) {
      return (
        <div ref={ref} {...props}>
          {renderActions()}
        </div>
      )
    }

    return (
      <Card ref={ref} className={className} {...props}>
        <CardHeader>
          <CardTitle className="text-lg">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          {renderActions()}
        </CardContent>
      </Card>
    )
  }
)

QuickActions.displayName = 'QuickActions'
